import { ModalProps, ResponseObject } from '@/types/shared'
import React from 'react'
import { Dialog } from 'vnetwork-ui'
import ObjectInfoForm from './ObjectInfoForm'
import { ObjectType, ShareRecipientResponse } from '@/modules/Drive/types'
import { t } from 'i18next'
import datacentral from '@/utils/axios/datacentral'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

export interface ObjectInfoModalProps extends ModalProps {
    objectName: string
    objectKey: string
    objectType: ObjectType
    size?: number
    lastModified?: string
}

export type ObjectInfo = {
    has_public_access: boolean
    owner_info: {
        employee_id: number
        employee_name: string
        employee_email_company: string
    }
}

const ObjectInfoModal: React.FC<ObjectInfoModalProps> = ({
    open,
    onClose,
    objectName,
    objectKey,
    objectType,
    size,
    lastModified,
}) => {
    const {
        data: objectInfo,
        isLoading,
        error,
    } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.DRIVE_OBJECT_INFO, objectKey],
        queryFn: async () => {
            const result = await datacentral.post<ResponseObject<ObjectInfo>>(
                '/drive/info',
                {
                    path: objectKey,
                }
            )
            return result.data.result
        },
        staleTime: 0,
    })

    const {
        data: sharedRecipientInfo,
        isLoading: isLoadingSharedRecipient,
        error: errorSharedRecipient,
    } = useQueryWithHandler<ShareRecipientResponse>({
        queryKey: [QUERY_KEYS.SHARE_RECIPIENTS, objectType, objectKey],
        queryFn: async () => {
            const result = await datacentral.post(
                '/drive/share/recipient-list',
                {
                    share_object_type: objectType,
                    share_object_key: objectKey,
                }
            )
            return result.data.results
        },
        enabled: !!objectType && !!objectKey,
        staleTime: 0,
    })

    return (
        <Dialog
            onClose={onClose}
            open={open}
            title={t('drive.object-info')}
            maxWidth="sm"
            children={
                <ObjectInfoForm
                    onClose={onClose}
                    objectName={objectName}
                    objectKey={objectKey}
                    objectType={objectType}
                    size={size}
                    lastModified={lastModified}
                    objectInfo={objectInfo}
                    sharedRecipientInfo={sharedRecipientInfo}
                    isLoading={isLoading || isLoadingSharedRecipient}
                    error={error || errorSharedRecipient}
                />
            }
            actions={null}
        />
    )
}

export default ObjectInfoModal
